import { useCallback, useRef, useState } from "react"

// Animation types (unused but kept for future use)
// type AnimationType = "doubleNod" | "wink3D" | "shocked" | "sideEye" | "wince"

export interface BrackyAnimationState {
  showWinkAnimation: boolean
  isBrackyHovered: boolean
  isBrackyPressed: boolean
  showSideEyeAnimation: boolean
  showInitialWinkAnimation: boolean
  isDoubleNodding: boolean
  isWinking3D: boolean
  isShocked: boolean
}

export function useBrackyAnimations() {
  const [showWinkAnimation, setShowWinkAnimation] = useState(false)
  const [isBrackyHovered, setIsBrackyHovered] = useState(false)
  const [isBrackyPressed, setIsBrackyPressed] = useState(false)
  const [showSideEyeAnimation, setShowSideEyeAnimation] = useState(false)
  const [showInitialWinkAnimation, setShowInitialWinkAnimation] = useState(false)
  const [isDoubleNodding, setIsDoubleNodding] = useState(false)
  const [isWinking3D, setIsWinking3D] = useState(false)
  const [isShocked, setIsShocked] = useState(false)

  const wink3DTimerRef = useRef<NodeJS.Timeout | null>(null)
  const shockedTimerRef = useRef<NodeJS.Timeout | null>(null)
  const sideEyeTimerRef = useRef<NodeJS.Timeout | null>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const isAnimationActive = useCallback(() => {
    return showSideEyeAnimation || isDoubleNodding || isWinking3D || isShocked
  }, [showSideEyeAnimation, isDoubleNodding, isWinking3D, isShocked])

  const clearAllTimers = useCallback(() => {
    if (wink3DTimerRef.current) {
      clearTimeout(wink3DTimerRef.current)
      wink3DTimerRef.current = null
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
      scrollTimeoutRef.current = null
    }
    if (shockedTimerRef.current) {
      clearTimeout(shockedTimerRef.current)
      shockedTimerRef.current = null
    }
    if (sideEyeTimerRef.current) {
      clearTimeout(sideEyeTimerRef.current)
      sideEyeTimerRef.current = null
    }
  }, [])

  const playDoubleNod = useCallback(() => {
    if (!isAnimationActive()) {
      setIsBrackyPressed(false)
      setIsDoubleNodding(true)

      setTimeout(() => {
        setIsDoubleNodding(false)
        setIsBrackyPressed(false)
        setIsBrackyHovered(false)
      }, 2000)
    }
  }, [isAnimationActive])

  const playWink3D = useCallback(
    (persistent = false) => {
      if (isWinking3D || isAnimationActive()) return

      if (wink3DTimerRef.current) {
        clearTimeout(wink3DTimerRef.current)
        wink3DTimerRef.current = null
      }

      setIsWinking3D(true)
      setIsBrackyHovered(false)
      setIsBrackyPressed(false)
      setShowWinkAnimation(false)

      if (!persistent) {
        wink3DTimerRef.current = setTimeout(() => {
          setIsWinking3D(false)
          wink3DTimerRef.current = null
        }, 2000) as unknown as NodeJS.Timeout
      }
    },
    [isWinking3D, isAnimationActive],
  )

  const playShocked = useCallback(
    (persistent = false) => {
      if (!isAnimationActive()) {
        if (shockedTimerRef.current) {
          clearTimeout(shockedTimerRef.current)
          shockedTimerRef.current = null
        }

        setIsShocked(true)

        if (!persistent) {
          shockedTimerRef.current = setTimeout(() => {
            setIsShocked(false)
            shockedTimerRef.current = null
          }, 2000) as unknown as NodeJS.Timeout
        }
      }
    },
    [isAnimationActive],
  )

  const playSideEye = useCallback(
    (persistent = false) => {
      if (!isAnimationActive()) {
        if (sideEyeTimerRef.current) {
          clearTimeout(sideEyeTimerRef.current)
          sideEyeTimerRef.current = null
        }

        setShowSideEyeAnimation(true)

        if (!persistent) {
          sideEyeTimerRef.current = setTimeout(() => {
            setShowSideEyeAnimation(false)
            sideEyeTimerRef.current = null
          }, 2000) as unknown as NodeJS.Timeout
        }
      }
    },
    [isAnimationActive],
  )

  const playWince = useCallback(
    (pressed = false) => {
      if (!isAnimationActive()) {
        setIsBrackyPressed(pressed)
        if (!pressed) {
          setIsBrackyHovered(true)
          setTimeout(() => {
            setIsBrackyHovered(false)
          }, 300)
        }
      }
    },
    [isAnimationActive],
  )

  const resetAnimations = useCallback(() => {
    clearAllTimers()
    setIsWinking3D(false)
    setIsDoubleNodding(false)
    setIsShocked(false)
    setShowSideEyeAnimation(false)
    setIsBrackyHovered(false)
    setIsBrackyPressed(false)
    setShowWinkAnimation(false)
  }, [clearAllTimers])

  const triggerInitialWink = useCallback(() => {
    setShowInitialWinkAnimation(true)

    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("brackyInitialComplete"))
      }
    }, 2750)

    setTimeout(() => {
      setShowInitialWinkAnimation(false)
    }, 4500)
  }, [])

  return {
    state: {
      showWinkAnimation,
      isBrackyHovered,
      isBrackyPressed,
      showSideEyeAnimation,
      showInitialWinkAnimation,
      isDoubleNodding,
      isWinking3D,
      isShocked,
    },
    actions: {
      playDoubleNod,
      playWink3D,
      playShocked,
      playSideEye,
      playWince,
      resetAnimations,
      triggerInitialWink,
      setIsBrackyHovered,
      setIsBrackyPressed,
      setShowWinkAnimation,
    },
    utils: {
      isAnimationActive,
      clearAllTimers,
    },
  }
}
